import { useState, useEffect } from 'react';
import WorkflowService from '../../../AppSettings/services/WorkflowService';
import './ProfileWorkflow.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faSearch,
  faEdit,
  faTrash,
  faTimes,
  faCheck,
} from '@fortawesome/free-solid-svg-icons';
import CreateModal from '../../../Components/CreateModal/CreateModal';
import AddButton from '../../../Components/AddButton/AddButton';
const ProfileWorkflow = () => {
  const [workflows, setWorkflows] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredWorkflows, setFilteredWorkflows] = useState([]);
  const [editingWorkflow, setEditingWorkflow] = useState(null);
  const [selectedWorkflows, setSelectedWorkflows] = useState({});
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [newWorkflow, setNewWorkflow] = useState({ name: '', milestones: [] });

  // Fetch workflows when the component mounts
  useEffect(() => {
    fetchWorkflows();
  }, []);

  // Auto-hide success message after 3 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const fetchWorkflows = async () => {
    try {
      const fetchedWorkflows = await WorkflowService.getWorkflows();
      const sanitizedWorkflows = fetchedWorkflows.map((workflow) => ({
        ...workflow,
        milestones: workflow.milestones || [],
      }));
      setWorkflows(sanitizedWorkflows);
      setFilteredWorkflows(sanitizedWorkflows);
      setError(null);
    } catch (error) {
      setError('An error occurred while fetching workflows.');
      console.error('Error fetching workflows:', error);
    }
  };

  // Filter workflows based on search query
  useEffect(() => {
    const filtered = workflows.filter(
      (workflow) =>
        (workflow.name &&
          workflow.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
        workflow.milestones?.some(
          (milestone) =>
            milestone &&
            milestone.toLowerCase().includes(searchQuery.toLowerCase())
        )
    );
    setFilteredWorkflows(filtered);
  }, [searchQuery, workflows]);

  const handleAddWorkflow = async (e) => {
    e.preventDefault();
    try {
      await WorkflowService.addWorkflow(newWorkflow);
      setIsModalOpen(false);
      setNewWorkflow({ name: '', milestones: [] });
      setError(null);
      setSuccess('Workflow created successfully!');
      // Refresh the workflows list to show the new one
      fetchWorkflows();
    } catch (error) {
      setError('An error occurred while adding the workflow.');
      console.error('Error adding workflow:', error);
    }
  };

  const handleUpdateWorkflow = async (workflowId) => {
    try {
      await WorkflowService.updateWorkflow(workflowId, editingWorkflow);
      setEditingWorkflow(null);
      fetchWorkflows();
      setError(null);
      setSuccess('Workflow updated successfully!');
    } catch (err) {
      setError('An error occurred while updating the workflow.');
      console.error(err);
    }
  };

  const handleDeleteWorkflow = async (workflowId) => {
    if (window.confirm('Are you sure you want to delete this workflow?')) {
      try {
        await WorkflowService.deleteWorkflow(workflowId);
        fetchWorkflows();
        setError(null);
        setSuccess('Workflow deleted successfully!');
      } catch (err) {
        setError('An error occurred while deleting the workflow.');
        console.error(err);
      }
    }
  };

  const toggleWorkflowSelection = (workflowId) => {
    setSelectedWorkflows((prev) => ({
      ...prev,
      [workflowId]: !prev[workflowId],
    }));
  };

  return (
    <div className="profile-workflow-container">
      <div className="profile-workflow-header">
        <h1 className="profile-workflow-title">Workflows</h1>
        <div className="header-actions">
          <div className="search-bar">
            <FontAwesomeIcon icon={faSearch} className="search-icon" />
            <input
              type="text"
              placeholder="Search workflows..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>
          <button className="add-button" onClick={() => setIsModalOpen(true)}>
            <FontAwesomeIcon icon={faPlus} />
          </button>
        </div>
      </div>

      {/* Success Message */}
      {success && (
        <div className="success-banner">
          <FontAwesomeIcon icon={faCheck} className="success-icon" />
          <span>{success}</span>
        </div>
      )}

      {/* Error Message */}
      {error && <p className="error-message">{error}</p>}

      {/* Workflows List */}
      <div className="profile-workflow-list">
        {filteredWorkflows.length === 0 ? (
          <p className="no-workflows-message">
            {searchQuery
              ? 'No matching workflows found.'
              : 'No workflows available.'}
          </p>
        ) : (
          <ul>
            {filteredWorkflows.map((workflow) => (
              <li key={workflow.workflowid} className="profile-workflow-item">
                <div className="workflow-checkbox">
                  <input
                    type="checkbox"
                    checked={!!selectedWorkflows[workflow.workflowid]}
                    onChange={() =>
                      toggleWorkflowSelection(workflow.workflowid)
                    }
                  />
                </div>
                {editingWorkflow &&
                editingWorkflow.workflowid === workflow.workflowid ? (
                  <div className="edit-workflow-form">
                    <input
                      type="text"
                      value={editingWorkflow.name}
                      onChange={(e) =>
                        setEditingWorkflow({
                          ...editingWorkflow,
                          name: e.target.value,
                        })
                      }
                    />
                    <div className="edit-actions">
                      <button
                        onClick={() =>
                          handleUpdateWorkflow(workflow.workflowid)
                        }
                      >
                        Save
                      </button>
                      <button onClick={() => setEditingWorkflow(null)}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="workflow-content">
                    <div className="workflow-details">
                      <strong>{workflow.name}</strong>
                      <div className="milestones-list">
                        {workflow.milestones?.map((milestone, index) => (
                          <span key={index} className="milestone-tag">
                            {milestone}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="workflow-actions">
                      <button onClick={() => setEditingWorkflow(workflow)}>
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button
                        onClick={() =>
                          handleDeleteWorkflow(workflow.workflowid)
                        }
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <CreateModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setNewWorkflow({ name: '', milestones: [] });
        }}
        title="Create Workflow"
        onSubmit={handleAddWorkflow}
        submitButtonText="Create Workflow"
      >
        <div className="workflow-form">
          <input
            type="text"
            placeholder="Workflow Name"
            value={newWorkflow.name}
            onChange={(e) =>
              setNewWorkflow({
                ...newWorkflow,
                name: e.target.value,
              })
            }
            required
          />
          <div className="milestones-input">
            <input
              type="text"
              placeholder="Add Milestone"
              value={newWorkflow.currentMilestone || ''}
              onChange={(e) =>
                setNewWorkflow({
                  ...newWorkflow,
                  currentMilestone: e.target.value,
                })
              }
            />
            <button
              type="button"
              className="Wrapper-button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (
                  newWorkflow.currentMilestone &&
                  newWorkflow.currentMilestone.trim()
                ) {
                  setNewWorkflow({
                    ...newWorkflow,
                    milestones: [
                      ...(newWorkflow.milestones || []),
                      newWorkflow.currentMilestone.trim(),
                    ],
                    currentMilestone: '',
                  });
                }
              }}
            >
              <AddButton />
            </button>
          </div>
          {newWorkflow.milestones && newWorkflow.milestones.length > 0 && (
            <div className="milestones-preview">
              <h3>Milestones:</h3>
              <ul>
                {newWorkflow.milestones.map((milestone, index) => (
                  <li key={index}>
                    {milestone}
                    <button
                      type="button"
                      onClick={() => {
                        const updatedMilestones = [...newWorkflow.milestones];
                        updatedMilestones.splice(index, 1);
                        setNewWorkflow({
                          ...newWorkflow,
                          milestones: updatedMilestones,
                        });
                      }}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </CreateModal>
    </div>
  );
};

export default ProfileWorkflow;
